/** Deterministic RNG utilities. Never use Math.random() in scene-affecting code. */

/** SplitMix32 — fast, good distribution, tiny state. */
export function makeRng(seed = 1337) {
  let a = seed >>> 0;
  const fn = () => {
    a = (a + 0x9e3779b9) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
    t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
    return ((t ^ (t >>> 15)) >>> 0) / 4294967296;
  };
  fn.range = (lo, hi) => lo + (hi - lo) * fn();
  fn.int = (lo, hi) => Math.floor(lo + (hi - lo + 1) * fn());
  fn.sign = () => (fn() < 0.5 ? -1 : 1);
  fn.pick = (arr) => arr[Math.floor(fn() * arr.length) % arr.length];
  fn.gauss = (mean = 0, sd = 1) => {
    // Box-Muller
    const u = Math.max(1e-7, fn());
    const v = fn();
    return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };
  fn.onSphere = (out) => {
    const z = fn() * 2 - 1;
    const t = fn() * Math.PI * 2;
    const r = Math.sqrt(1 - z * z);
    out.set(r * Math.cos(t), r * Math.sin(t), z);
    return out;
  };
  fn.reseed = (s) => { a = s >>> 0; };
  return fn;
}

export const rand = makeRng(0xc0ffee);

/* ---- value / gradient noise (deterministic, no tables allocated per call) ---- */

const P = new Uint8Array(512);
{
  const src = new Uint8Array(256);
  for (let i = 0; i < 256; i++) src[i] = i;
  const r = makeRng(20240613);
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const t = src[i]; src[i] = src[j]; src[j] = t;
  }
  for (let i = 0; i < 512; i++) P[i] = src[i & 255];
}

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a, b, t) => a + (b - a) * t;

function grad3(h, x, y, z) {
  switch (h & 15) {
    case 0: return x + y; case 1: return -x + y; case 2: return x - y; case 3: return -x - y;
    case 4: return x + z; case 5: return -x + z; case 6: return x - z; case 7: return -x - z;
    case 8: return y + z; case 9: return -y + z; case 10: return y - z; case 11: return -y - z;
    case 12: return y + x; case 13: return -y + z; case 14: return y - x; default: return -y - z;
  }
}

/** Classic Perlin, range about [-1, 1]. */
export function perlin3(x, y, z) {
  const X = Math.floor(x) & 255, Y = Math.floor(y) & 255, Z = Math.floor(z) & 255;
  x -= Math.floor(x); y -= Math.floor(y); z -= Math.floor(z);
  const u = fade(x), v = fade(y), w = fade(z);
  const A = P[X] + Y, AA = P[A] + Z, AB = P[A + 1] + Z;
  const B = P[X + 1] + Y, BA = P[B] + Z, BB = P[B + 1] + Z;
  return lerp(
    lerp(
      lerp(grad3(P[AA], x, y, z), grad3(P[BA], x - 1, y, z), u),
      lerp(grad3(P[AB], x, y - 1, z), grad3(P[BB], x - 1, y - 1, z), u), v),
    lerp(
      lerp(grad3(P[AA + 1], x, y, z - 1), grad3(P[BA + 1], x - 1, y, z - 1), u),
      lerp(grad3(P[AB + 1], x, y - 1, z - 1), grad3(P[BB + 1], x - 1, y - 1, z - 1), u), v),
    w);
}

export const perlin2 = (x, y) => perlin3(x, y, 0.31830988);

/** Fractal Brownian motion. */
export function fbm2(x, y, octaves = 5, lacunarity = 2.0, gain = 0.5) {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * perlin2(x * freq, y * freq);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Ridged multifractal — good for rock, cracks, rust edges. */
export function ridged2(x, y, octaves = 5, lacunarity = 2.0, gain = 0.5) {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(perlin2(x * freq, y * freq));
    sum += amp * n * n;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Worley / cellular noise. Returns { f1, f2, id } — tileable over `period`. */
export function worley2(x, y, period = 0) {
  const xi = Math.floor(x), yi = Math.floor(y);
  let f1 = 1e9, f2 = 1e9, id = 0;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      let cx = xi + ox, cy = yi + oy;
      let hx = cx, hy = cy;
      if (period > 0) { hx = ((cx % period) + period) % period; hy = ((cy % period) + period) % period; }
      const h = P[(P[hx & 255] + (hy & 255)) & 511];
      const px = cx + (h & 15) / 15;
      const py = cy + ((h >> 4) & 15) / 15;
      const dx = px - x, dy = py - y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < f1) { f2 = f1; f1 = d; id = h; }
      else if (d < f2) { f2 = d; }
    }
  }
  return { f1, f2, id };
}

/** Tileable fbm on a torus — use for seamless textures. */
export function tileableFbm(u, v, freq, octaves = 5, gain = 0.5) {
  // Sample 4D-ish by wrapping through two circles projected to 3D perlin.
  let amp = 1, f = freq, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const a = u * Math.PI * 2, b = v * Math.PI * 2;
    const nx = Math.cos(a) * f / (Math.PI * 2);
    const ny = Math.sin(a) * f / (Math.PI * 2);
    const nz = Math.cos(b) * f / (Math.PI * 2);
    const nw = Math.sin(b) * f / (Math.PI * 2);
    sum += amp * (perlin3(nx, ny, nz) * 0.5 + perlin3(ny, nz, nw) * 0.5);
    norm += amp;
    amp *= gain;
    f *= 2;
  }
  return sum / norm;
}

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const smoothstep = (e0, e1, x) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
export const mix = lerp;
